/**
 * TrendsPage — `/trends`.
 *
 * 7-day overview of every metric the engine tracks. Each tile is a
 * sparkline + last value + delta vs the start of the window. Powered by
 * the existing /api/dashboard/history endpoint so we don't have to write
 * a single new backend route.
 *
 * Layout matches the rest of the Pulse pages so the user can drill from
 * any tile into the dedicated detail page.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Droplets, Leaf, Wind, Thermometer, Home, Clock, BarChart3, Bell, Settings as SettingsIcon, CloudRain } from 'lucide-react';
import { PulseSparkline } from '../../components/pulse/PulseSparkline';
import { useUnreadAlerts } from '../../hooks/useUnreadAlerts';
import { AlertDot } from '../../components/pulse/AlertDot';

const TILES = [
  { metric: 'moisture',  label: 'Soil Moisture', unit: '%',     icon: Droplets,    color: 'text-sky-400',    route: '/pulse/moisture' },
  { metric: 'ec',        label: 'EC (Soil)',     unit: '',      icon: Leaf,        color: 'text-emerald-400', route: '/pulse/ec' },
  { metric: 'vpd',       label: 'VPD',           unit: ' kPa',  icon: Wind,        color: 'text-purple-400',  route: '/pulse/vpd' },
  { metric: 'dewpoint',  label: 'Dewpoint',      unit: '°F',    icon: CloudRain,   color: 'text-amber-400',   route: null },
  { metric: 'temp',      label: 'Air Temp',      unit: '°F',    icon: Thermometer, color: 'text-rose-400',    route: null },
  { metric: 'soil_temp', label: 'Soil Temp',     unit: '°F',    icon: Thermometer, color: 'text-orange-400',  route: null },
];

export default function TrendsPage() {
  const navigate = useNavigate();
  const { total: unreadTotal, high: unreadHigh } = useUnreadAlerts();

  return (
    <div className="min-h-screen bg-black text-slate-100 pb-24">
      <div className="max-w-md mx-auto px-4 pt-3">
        <div className="flex items-center justify-between py-2">
          <button data-testid="trends-back"
                  onClick={() => navigate('/pulse')}
                  className="p-2 -ml-2 rounded-full hover:bg-slate-800">
            <ChevronLeft className="w-6 h-6 text-slate-200" />
          </button>
          <div className="text-base font-semibold tracking-tight">Trends · 7 day</div>
          <span className="w-9" />
        </div>

        <div className="text-xs text-slate-400 px-1 mb-3">
          The numbers behind every recommendation — at a glance.
        </div>

        <div className="space-y-3" data-testid="trends-grid">
          {TILES.map(t => {
            const Icon = t.icon;
            return (
              <div key={t.metric}
                   data-testid={`trend-tile-${t.metric}`}
                   onClick={() => t.route && navigate(t.route)}
                   className={`bg-slate-900/80 border border-slate-800 rounded-2xl p-3 ${t.route ? 'cursor-pointer hover:border-slate-700' : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${t.color}`} />
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">
                    {t.label}
                  </span>
                </div>
                <PulseSparkline metric={t.metric} hours={168} unit={t.unit} height={140} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Same 5-icon bottom nav as the rest of the Pulse pages */}
      <div className="fixed bottom-0 inset-x-0 bg-slate-950/95 backdrop-blur border-t border-slate-800 z-30">
        <div className="max-w-[calc(100%-185px)] px-3 py-1.5 grid grid-cols-5 text-center text-[10px] text-slate-400 gap-1">
          <button data-testid="nav-home"     onClick={() => navigate('/pulse')}    className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><Home className="w-5 h-5"/><span>Home</span></button>
          <button data-testid="nav-history"  onClick={() => navigate('/activity')} className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><Clock className="w-5 h-5"/><span>History</span></button>
          <button data-testid="nav-trends"   className="flex flex-col items-center gap-1 py-1.5 text-emerald-400"><BarChart3 className="w-5 h-5"/><span>Trends</span></button>
          <button data-testid="nav-alerts"   onClick={() => navigate('/alerts')}   className="relative flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><span className="relative"><Bell className="w-5 h-5"/><AlertDot total={unreadTotal} high={unreadHigh} /></span><span>Alerts</span></button>
          <button data-testid="nav-settings" onClick={() => navigate('/profile')}  className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><SettingsIcon className="w-5 h-5"/><span>Settings</span></button>
        </div>
      </div>
    </div>
  );
}
